import React, { useContext } from "react";
import { Checkbox, Button } from "antd";
import { useDispatch, useSelector } from "react-redux";

import { setUserFilters } from "../../../../redux/slices/leadSlice";
import { UserContext } from "../../../../amplify/authenticator-provider";
import useFetchUsers from "../../../../hooks/useFetchUsers";
import CustomSkeleton from "../../../commonComponents/CustomSkeleton";

const UserFiltersContent = ({
  variables,
  setVariables,
  scrollListToTop,
  clearUrl,
  search = "",
  onFilterChange,
  onClear,
}) => {
  const dispatch = useDispatch();
  const { user } = useContext(UserContext);
  const userFilters = useSelector(state => state.lead.filters.userFilters);

  // Hook auto-applies active-user filter when location's SHOW_ACTIVATE_USER_ONLY setting is true.
  const { userList, loading } = useFetchUsers(null, null, !user?.company?.id, {
    fetchPolicy: "cache-first",
    isCurrentLocationUser: true,
    isAllLocationUsers: true,
  });

  if (loading) {
    return (
      <div className="px-3 py-2">
        <CustomSkeleton
          skeletonCount={5}
          skeletonMarginBottom="7px"
          skeletonWidth="28rem"
          skeletonHeight="2rem"
        />
      </div>
    );
  }

  const filteredUsers = userList
    .filter(u => {
      const fullName = `${u.firstName || ""} ${u.lastName || ""}`.trim();
      return fullName.toLowerCase().includes(search.toLowerCase());
    })
    .sort((a, b) => {
      const nameA = `${a.firstName || ""} ${a.lastName || ""}`.trim().toLowerCase();
      const nameB = `${b.firstName || ""} ${b.lastName || ""}`.trim().toLowerCase();
      return nameA.localeCompare(nameB);
    });

  const handleToggle = userId => {
    // Normalize to numbers so we don't get bitten by string-vs-number
    // mismatches between drill-in dispatches (which coerce to Number) and
    // raw GraphQL ids (often strings).
    const idNum = +userId;
    const current = userFilters.map(Number);
    const newFilters = current.includes(idNum)
      ? current.filter(id => id !== idNum)
      : [...current, idNum];
    setVariables?.({ ...variables, page: 1 });
    dispatch(setUserFilters(newFilters));
    scrollListToTop?.();
    clearUrl?.();
    onFilterChange?.();
  };

  return (
    <div className="flex flex-col px-3 py-2">
      {onClear && userFilters.length > 0 && (
        <div className="flex justify-end mb-1">
          <Button type="link" size="small" className="!p-0 !h-auto" onClick={onClear}>
            Clear
          </Button>
        </div>
      )}
      {filteredUsers.map(u => {
        const fullName = `${u.firstName || ""} ${u.lastName || ""}`.trim();
        const role = u.companyRole?.name || "";
        return (
          <div
            key={u.id}
            className="flex items-center gap-2 py-2 px-1 cursor-pointer hover:bg-gray-50 rounded"
            onClick={() => handleToggle(u.id)}
          >
            <Checkbox checked={userFilters.map(Number).includes(+u.id)} />
            <span className="text-sm text-black-88">
              {fullName}
              {role && <span className="text-[#00000073] text-xs ml-1">({role})</span>}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default UserFiltersContent;
